import React, { useState } from 'react';
import { Box, Typography, Avatar, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Link as LinkIcon, MoreVertical, Trash2, MessageSquarePlus } from 'lucide-react';
import { Conversation } from '../../../types';
import { users, cases } from '../../../data/sampleData';
import { useMessageStore } from '../../../store/messageStore';

interface ThreadHeaderProps {
  conversation: Conversation;
}

export const ThreadHeader: React.FC<ThreadHeaderProps> = ({ conversation }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { deleteConversation, startConversation, setActiveConversation } = useMessageStore();
  const currentUser = users[0];
  const otherParticipant = users.find(
    user => conversation.participants.includes(user.id) && user.id !== currentUser.id
  );
  const caseDetails = conversation.caseId 
    ? cases.find(c => c.id === conversation.caseId)
    : null;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = () => {
    handleMenuClose();
    deleteConversation(conversation.id);
  };

  const handleNewMessage = () => {
    handleMenuClose();
    const newConversationId = startConversation(conversation.participants, conversation.caseId);
    setActiveConversation(newConversationId);
  };

  return (
    <Box 
      sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        bgcolor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 1,
      }}
    >
      <Avatar 
        src={otherParticipant?.avatar}
        alt={otherParticipant?.name}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          component="div" 
          sx={{ 
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {otherParticipant?.name}
        </Typography>
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{
            display: 'block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {otherParticipant?.role}
        </Typography>
      </Box>
      {caseDetails && (
        <Chip
          icon={<LinkIcon size={16} />}
          label={`Case ${caseDetails.caseNumber}`}
          color="primary"
          variant="outlined"
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
      )}
      <IconButton onClick={handleMenuOpen}>
        <MoreVertical size={20} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleNewMessage}>
          <ListItemIcon>
            <MessageSquarePlus size={18} />
          </ListItemIcon>
          <ListItemText>New Message</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Trash2 size={18} className="text-red-500" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Conversation</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};